import React, { useState } from 'react';
import axios from 'axios';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { useLanguage } from '../../lib/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Contact = () => {
  const { t, lang } = useLanguage();
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleChange = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) return;
    setLoading(true);
    try {
      await axios.post(`${API}/consultations`, { ...form, language: lang });
      setDone(true);
      toast.success(t.contact.success);
      setForm({ name: '', email: '', phone: '', message: '' });
    } catch (err) {
      toast.error(t.contact.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-16 md:py-20 bg-sand-100 relative overflow-hidden">
      <div className="absolute inset-0 opacity-30 bg-[radial-gradient(circle_at_top_right,#D27E6A40,transparent_50%)]" />
      <div className="relative max-w-6xl mx-auto px-6 md:px-12 lg:px-16 grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
        <div className="lg:col-span-5">
          <div className="text-xs uppercase tracking-[0.24em] text-sand-600 mb-6">{t.contact.eyebrow}</div>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-ink-900 leading-[1.1] mb-6">
            {t.contact.title}
          </h2>
          <p className="text-base md:text-lg text-ink-500 leading-relaxed">{t.contact.sub}</p>

          <div className="mt-10 pt-10 border-t border-sand-300">
            <p className="text-sm text-ink-500 leading-relaxed">{t.contact.legal}</p>
          </div>
        </div>

        <div className="lg:col-span-7">
          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="bg-cream border border-sand-200 rounded-sm p-8 md:p-10 shadow-[0_24px_60px_rgba(45,51,47,0.06)]"
          >
            {done ? (
              <div data-testid="contact-success" className="flex flex-col items-start gap-4 py-8">
                <div className="w-12 h-12 rounded-full bg-olive-500/15 text-olive-600 flex items-center justify-center">
                  <CheckCircle2 className="w-6 h-6" />
                </div>
                <h3 className="font-serif text-2xl text-ink-900">{t.contact.success}</h3>
                <button
                  type="button"
                  data-testid="contact-reset-button"
                  onClick={() => setDone(false)}
                  className="text-sm underline underline-offset-4 text-ink-700 hover:text-sand-600"
                >
                  ↻
                </button>
              </div>
            ) : (
              <>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="name" className="text-xs uppercase tracking-[0.18em] text-ink-500">{t.contact.labels.name}</Label>
                    <Input
                      id="name"
                      data-testid="contact-input-name"
                      required
                      value={form.name}
                      onChange={handleChange('name')}
                      placeholder={t.contact.placeholders.name}
                      className="mt-2 bg-cream border-sand-200 rounded-sm h-12 text-ink-900 focus-visible:ring-sand-500"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-xs uppercase tracking-[0.18em] text-ink-500">{t.contact.labels.phone}</Label>
                    <Input
                      id="phone"
                      data-testid="contact-input-phone"
                      required
                      value={form.phone}
                      onChange={handleChange('phone')}
                      placeholder={t.contact.placeholders.phone}
                      className="mt-2 bg-cream border-sand-200 rounded-sm h-12 text-ink-900 focus-visible:ring-sand-500"
                    />
                  </div>
                </div>

                <div className="mt-5">
                  <Label htmlFor="email" className="text-xs uppercase tracking-[0.18em] text-ink-500">{t.contact.labels.email}</Label>
                  <Input
                    id="email"
                    type="email"
                    data-testid="contact-input-email"
                    required
                    value={form.email}
                    onChange={handleChange('email')}
                    placeholder={t.contact.placeholders.email}
                    className="mt-2 bg-cream border-sand-200 rounded-sm h-12 text-ink-900 focus-visible:ring-sand-500"
                  />
                </div>

                <div className="mt-5">
                  <Label htmlFor="message" className="text-xs uppercase tracking-[0.18em] text-ink-500">{t.contact.labels.message}</Label>
                  <Textarea
                    id="message"
                    data-testid="contact-input-message"
                    value={form.message}
                    onChange={handleChange('message')}
                    placeholder={t.contact.placeholders.message}
                    rows={5}
                    className="mt-2 bg-cream border-sand-200 rounded-sm text-ink-900 focus-visible:ring-sand-500 resize-none"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-submit-button"
                  className="mt-8 w-full sm:w-auto inline-flex items-center justify-center gap-3 px-8 py-4 bg-ink-900 text-cream rounded-sm text-sm tracking-wide hover:bg-sand-600 transition-colors disabled:opacity-60"
                >
                  {loading ? t.contact.sending : t.contact.cta}
                  {!loading && <ArrowRight className="w-4 h-4" />}
                </button>
              </>
            )}
          </form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
